/**
 * impersonation.ts — start/manage real customer-JWT impersonation sessions.
 *
 * Flow:
 *   1. startImpersonation(customerId, orgName)
 *      a. GET /customers/:id/contacts → pick the primary (contact_firstable) or first contact
 *      b. POST /partner/auth/impersonate {contact_id, realm:"partner"} (uses dataCentralApi —
 *         employee ACCESS token, NOT the RPT — DC gateway validates via employee middleware)
 *      c. Store returned customer access_token + impersonation_token in impersonation-session
 *
 * The DC route: POST /api/v4/partner/auth/impersonate
 * Auth: plain employee ACCESS token (not UMA RPT). DC VerifyAuthToken validates the
 * employee, then the impersonate controller does a Keycloak token-exchange for the contact.
 *
 * Why this is correct: see phase-07b plan — X-Act-As-Org only works on service-scoped
 * endpoints; domain-scoped WAAP/CDN endpoints resolve org from the JWT's customer_id
 * claim. Real customer token resolves correctly everywhere.
 */

import { dataCentralApi } from '@/api/client';
import {
  startImpersonationSession,
  type ImpersonationSession,
} from '@/lib/impersonation-session';

/* ── DC response types ──────────────────────────────────────────────────── */

interface DCContact {
  customer_contact_id: number;
  contact_email: string;
  contact_firstable?: boolean;
  authentication_method?: string;
}

interface DCContactsResponse {
  results?: DCContact[];
  data?: DCContact[];
}

interface DCImpersonateResult {
  access_token?: string;
  impersonation_token?: string;
  exp?: number;
  uuid?: string;
  auth_method?: string;
  impersonated?: boolean;
  impersonator_name?: string;
  impersonator_email?: string;
  impersonator_id?: number;
}

interface DCImpersonateResponse {
  result?: DCImpersonateResult;
  message?: string;
}

/* ── Exported error types ───────────────────────────────────────────────── */

export class ImpersonationError extends Error {
  constructor(
    message: string,
    public readonly code: 'NO_CONTACT' | 'NO_TOKEN' | 'API_ERROR',
  ) {
    super(message);
    this.name = 'ImpersonationError';
  }
}

/* ── Core function ──────────────────────────────────────────────────────── */

/**
 * Resolve the primary (or first) impersonatable contact for a customer, then
 * POST /partner/auth/impersonate to get a real customer JWT. Stores the
 * session in sessionStorage and fires the 'impersonation-change' event.
 *
 * @param customerId  DC customer_id (== organization_id in @mgmt)
 * @param orgName     Display name for the banner (company name or fallback)
 * @returns The started ImpersonationSession
 * @throws ImpersonationError on missing contact or API failure
 */
export async function startImpersonation(
  customerId: number,
  orgName: string,
): Promise<ImpersonationSession> {
  // 1. Resolve the primary (or first keycloak-capable) contact for this customer.
  const contactsRes = await dataCentralApi.get<DCContactsResponse>(
    `/customers/${customerId}/contacts?page=1&page_size=50`,
  );
  const contacts: DCContact[] =
    contactsRes.data?.results ?? contactsRes.data?.data ?? [];

  // Prefer the primary contact with a keycloak account; fallback to first keycloak contact.
  const primary = contacts.find(
    (c) => c.contact_firstable && c.authentication_method === 'keycloak',
  );
  const fallback = contacts.find((c) => c.authentication_method === 'keycloak');
  const contact = primary ?? fallback;

  if (!contact) {
    throw new ImpersonationError(
      `No impersonatable contact found for customer ${customerId}`,
      'NO_CONTACT',
    );
  }

  // 2. POST /partner/auth/impersonate — DC does Keycloak token-exchange for the contact.
  let impRes: DCImpersonateResponse;
  try {
    const res = await dataCentralApi.post<DCImpersonateResponse>(
      '/partner/auth/impersonate',
      { contact_id: contact.customer_contact_id, realm: 'partner' },
    );
    impRes = res.data;
  } catch (e) {
    throw new ImpersonationError(
      `DC impersonate request failed: ${String(e)}`,
      'API_ERROR',
    );
  }

  const result = impRes.result;
  if (!result?.access_token) {
    throw new ImpersonationError(
      'DC impersonate returned no access_token',
      'NO_TOKEN',
    );
  }

  // 3. Store the session.
  const session: ImpersonationSession = {
    customerToken: result.access_token,
    impersonationToken: result.impersonation_token ?? '',
    orgId: String(customerId),
    orgName,
    impersonatorName: result.impersonator_name ?? '',
    impersonatorEmail: result.impersonator_email ?? '',
    expSeconds: result.exp ?? Math.floor(Date.now() / 1000) + 3600,
    contactId: contact.customer_contact_id,
  };

  return startImpersonationSession(session);
}
